import React, { createContext, useCallback, useContext, useRef, useState, ReactNode } from 'react';
import { trackLaikaChatOpened } from '../analytics';

interface ChatContextType {
    isChatOpen: boolean;
    openChat: (source?: string) => void;
    closeChat: () => void;
    toggleChat: (source?: string) => void;
    dockTarget: HTMLElement | null;
    setDockTarget: (el: HTMLElement | null) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [dockTarget, setDockTarget] = useState<HTMLElement | null>(null);
    const isOpenRef = useRef(false);

    const openChat = useCallback((source: string = 'unknown') => {
        if (!isOpenRef.current) {
            trackLaikaChatOpened(source);
        }
        isOpenRef.current = true;
        setIsChatOpen(true);
    }, []);

    const closeChat = useCallback(() => {
        isOpenRef.current = false;
        setIsChatOpen(false);
    }, []);

    const toggleChat = useCallback((source: string = 'toggle') => {
        if (isOpenRef.current) {
            closeChat();
        } else {
            openChat(source);
        }
    }, [closeChat, openChat]);

    return (
        <ChatContext.Provider value={{ isChatOpen, openChat, closeChat, toggleChat, dockTarget, setDockTarget }}>
            {children}
        </ChatContext.Provider>
    );
};

export const useChat = (): ChatContextType => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
};
